import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "./Reveal";

const LETTERS = [
  {
    id: "01",
    kicker: "Letter from the atelier — No. 01",
    subtitle: null,
    stanzas: [
      { t: "p", x: "There are things we keep because they are beautiful.\nAnd then there are things we keep because, somehow, they have become part of us." },
      { t: "p", x: "Welcome to The Things We Keep.\nThis little corner of my world began with a fascination for objects that carry something with them — a history, a memory, a person, a place." },
      { t: "p", x: "Old wood marked by time.\nAntlers carried through wild landscapes.\nA piece of fabric that once belonged to someone.\nA forgotten object found in the back of a drawer." },
      { t: "p", x: "Things that might mean very little to one person, and everything to another." },
      { t: "p", x: "In the atelier, I bring these fragments together and allow them to become something new — not to erase what they were, but to let their story continue." },
      { t: "p", x: "These letters will be part of that story.\nI'll share pieces while they are still becoming. Things I find. Things I keep. The stories behind finished works. Thoughts, photographs and little glimpses into the beautiful mess of creating." },
      { t: "p", x: "Some letters may be long.\nSome may contain only a photograph and a few words.\nThey will come when there is something worth sending." },
      { t: "p", x: "Thank you for stepping inside.\nUntil the next thing finds its way to me," },
    ],
    sign: ["With love,", "Carine"],
    signRole: null,
    closing: "Not everything we keep belongs in a box.",
  },
  {
    id: "02",
    kicker: "Letter from the atelier — No. 02",
    subtitle: "When many hands become one story",
    stanzas: [
      { t: "p", x: "Dear you," },
      { t: "p", x: "Some pieces begin with an object.\nThis one began with a gathering." },
      { t: "p", x: "When my friend Heidrun was expecting her baby girl, we gathered for her Mother Blessing. Before that day, I had begun building a mobile for her — a structure of circles, antler, wood and fragments.\nBut I never intended to finish it alone.\nI asked each person coming to bring something.\nNot something valuable.\nSomething meaningful." },
      { t: "p", x: "A small object, a fragment, a bead, a feather — anything carrying a memory, a feeling, a wish or an intention for Heidrun and the little girl making her way into the world." },
      { t: "p", x: "And then, together, we added them.\nSome attached their own pieces.\nSome placed them in my hands.\nSome told the story of what they had brought.\nSlowly, the mobile changed.\nIt stopped being something I had made for Heidrun and became something we had made around her." },
      { t: "p", x: "And I think this was the moment I understood something important about The Things We Keep." },
      { t: "pull", x: "An artwork doesn't always have to tell the story of one person.\nSometimes it can hold a moment between people." },
      { t: "p", x: "The finished mobile carries traces of everyone who gathered around Heidrun at that particular threshold in her life. Years from now, her daughter may look at it and know that before she was even born, there was already a circle of people waiting for her." },
      { t: "p", x: "Alongside the piece, I wrote a fairytale for Heidrun and Florian — my own interpretation of the strange and beautiful paths that had brought them to one another, and eventually to her." },
      { t: "p", x: "So the work became two things.\nOne part can be touched.\nThe other can be told.\nOne carries objects.\nThe other carries words.\nTogether, they hold a moment that will never exist in quite the same way again." },
      { t: "p", x: "And perhaps that is what I am trying to do with all of this.\nNot preserve things exactly as they were.\nBut give them somewhere to continue." },
      { t: "pull", x: "No single fragment tells the story.\nTogether, they do." },
    ],
    sign: ["With love,", "Carine"],
    signRole: null,
    closing: null,
  },
];

function Lines({ text }) {
  return text.split("\n").map((line, j) => (
    <span key={j}>
      {j > 0 && <br />}
      {line}
    </span>
  ));
}

export default function LettersPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Letters — The Things We Keep";
    return () => {
      document.title = "The Things We Keep — Objects with a story";
    };
  }, []);

  return (
    <div className="bg-paper pb-24 pt-32 sm:pb-32 sm:pt-40" data-testid="letters-page">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal>
          <p className="ttwk-label" data-testid="letters-eyebrow">
            From the atelier
          </p>
          <h1 className="mt-6 font-serif text-5xl font-light tracking-tight sm:text-6xl" data-testid="letters-title">
            Letters
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-umber/80">
            Occasional letters, written by hand and sent to the list. They are kept here, so they can be read again.
          </p>
        </Reveal>

        {LETTERS.map((letter) => (
          <Reveal key={letter.id}>
            <article
              className="img-frame mt-14 rounded-sm border border-clay/60 bg-cream p-8 sm:p-12"
              data-testid={`letter-${letter.id}`}
            >
              <p className="text-[11px] uppercase tracking-[0.25em] text-terracotta">{letter.kicker}</p>
              {letter.subtitle && (
                <p className="mt-2 font-serif text-xl italic text-espresso sm:text-2xl">{letter.subtitle}</p>
              )}
              <div className="mt-8">
                {letter.stanzas.map((s, i) =>
                  s.t === "pull" ? (
                    <p key={i} className="my-8 font-serif text-xl italic leading-snug text-terracotta sm:text-2xl">
                      <Lines text={s.x} />
                    </p>
                  ) : (
                    <p key={i} className="mt-0 mb-6 font-serif text-lg font-light leading-relaxed text-umber sm:text-xl">
                      <Lines text={s.x} />
                    </p>
                  )
                )}
              </div>
              <p className="mt-10 font-serif text-lg italic leading-relaxed text-espresso">
                <Lines text={letter.sign.join("\n")} />
              </p>
              {letter.signRole && <p className="mt-1 text-xs uppercase tracking-[0.2em] text-umber/50">{letter.signRole}</p>}
              {letter.closing && (
                <p className="mt-10 border-t border-clay/60 pt-8 text-center font-serif text-xl italic text-terracotta sm:text-2xl">
                  {letter.closing}
                </p>
              )}
            </article>
          </Reveal>
        ))}

        <Reveal>
          <button
            onClick={() => navigate("/", { state: { scrollTo: "signup" } })}
            className="mt-16 rounded-full bg-terracotta px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-espresso"
            data-testid="letters-cta"
          >
            Stay in touch
          </button>
        </Reveal>
      </div>
    </div>
  );
}
